import { useId, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { layout, space } from "@tokens/index";
import { Surface } from "@primitives/Surface";
import { Text } from "@primitives/Text";

/**
 * SettingsGroup: a titled set of `SettingsRow`s.
 *
 * The title and description sit above a flat card (`Surface` at elevation 1,
 * `lg` radius, hairline border, no shadow). Rows inside the card draw their
 * own inset dividers. The group is a `section` labelled by its title.
 */

export interface SettingsGroupProps {
    readonly title?: string;
    readonly description?: ReactNode;
    readonly children: ReactNode;
    readonly testId?: string;
}

const groupStyle: CSSProperties = {
    display: "grid",
    gap: space[2],
    width: "100%",
    minWidth: 0,
};

// Aligns the heading with the row label edge (`--row-px` in SettingsRow).
const headingStyle: CSSProperties = {
    display: "grid",
    gap: space[1],
    paddingInline: space[4],
    minWidth: 0,
};

const cardStyle: CSSProperties = {
    width: "100%",
    minWidth: 0,
};

const listStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "inherit",
};

export function SettingsGroup({
    title,
    description,
    children,
    testId,
}: SettingsGroupProps): ReactElement {
    const titleId = useId();
    const hasHeading = title !== undefined || description !== undefined;

    return (
        <section
            aria-labelledby={title !== undefined ? titleId : undefined}
            data-testid={testId}
            style={groupStyle}
        >
            {hasHeading && (
                <div style={headingStyle}>
                    {title !== undefined && (
                        <Text as="h2" id={titleId} role="bodySm" weight="semibold" tone="muted">
                            {title}
                        </Text>
                    )}
                    {description !== undefined && (
                        <Text
                            as="p"
                            role="bodySm"
                            tone="muted"
                            style={{ maxWidth: layout.proseMaxWidth }}
                        >
                            {description}
                        </Text>
                    )}
                </div>
            )}
            <Surface elevation={1} borderRadius="lg" style={cardStyle}>
                <div style={listStyle}>{children}</div>
            </Surface>
        </section>
    );
}
